import type React from 'react';

export type TypographyPresetKey =
  | 'heroTitle'
  | 'heroSubtitle'
  | 'heroMicroLine'
  | 'sectionHeading'
  | 'body'
  | 'button'
  | 'caption';

export const TYPOGRAPHY_PRESET_KEYS: TypographyPresetKey[] = [
  'heroTitle',
  'heroSubtitle',
  'heroMicroLine',
  'sectionHeading',
  'body',
  'button',
  'caption',
];

export interface TypographyPreset {
  fontFamilyKey: string;
  fontWeight: number;
  letterSpacing: number;
  lineHeight: number;
  size: {
    mobile: number;
    desktop: number;
  };
  color?: string;
}

export interface HeroReadabilityDefaults {
  backdrop: 'none' | 'subtle' | 'strong';
  textShadow: 'none' | 'soft' | 'strong';
  autoContrast: boolean;
}

export interface DesignSystemTypography {
  fontLibrary: Record<string, string>;
  presets: Record<TypographyPresetKey, TypographyPreset>;
  heroReadabilityDefaults: HeroReadabilityDefaults;
}

export const TYPOGRAPHY_PRESET_LABELS: Record<TypographyPresetKey, string> = {
  heroTitle: 'Hero Title',
  heroSubtitle: 'Hero Subtitle',
  heroMicroLine: 'Hero Micro-line',
  sectionHeading: 'Section Heading',
  body: 'Body',
  button: 'Button',
  caption: 'Caption',
};

const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 1000;
const MIN_LINE_HEIGHT = 1.1;
const MAX_LINE_HEIGHT = 1.8;
const MIN_LETTER_SPACING = -0.08;
const MAX_LETTER_SPACING = 0.25;
const MIN_FONT_WEIGHT = 300;
const MAX_FONT_WEIGHT = 800;

export const DEFAULT_FONT_LIBRARY: Record<string, string> = {
  inter: '"Inter", "Segoe UI", system-ui, sans-serif',
  playfair: '"Playfair Display", Georgia, serif',
  dmSans: '"DM Sans", "Inter", "Segoe UI", system-ui, sans-serif',
  'TheArtist Script': '"TheArtist Script", cursive',
  cormorant: '"Cormorant Garamond", Georgia, serif',
  manrope: '"Manrope", "Inter", "Segoe UI", system-ui, sans-serif',
  spaceGrotesk: '"Space Grotesk", "Inter", "Segoe UI", system-ui, sans-serif',
  crimsonPro: '"Crimson Pro", Georgia, serif',
  syne: '"Syne", "Inter", "Segoe UI", system-ui, sans-serif',
  bebasNeue: '"Bebas Neue", "Arial Narrow", sans-serif',
  outfit: '"Outfit", "Inter", "Segoe UI", system-ui, sans-serif',
  plusJakartaSans: '"Plus Jakarta Sans", "Inter", "Segoe UI", system-ui, sans-serif',
  montserrat: '"Montserrat", "Inter", "Segoe UI", system-ui, sans-serif',
  raleway: '"Raleway", "Inter", "Segoe UI", system-ui, sans-serif',
  oswald: '"Oswald", "Arial Narrow", sans-serif',
  archivo: '"Archivo", "Inter", "Segoe UI", system-ui, sans-serif',
  bricolageGrotesque: '"Bricolage Grotesque", "Inter", "Segoe UI", system-ui, sans-serif',
  fraunces: '"Fraunces", Georgia, serif',
  lora: '"Lora", Georgia, serif',
  libreBaskerville: '"Libre Baskerville", Georgia, serif',
  merriweather: '"Merriweather", Georgia, serif',
  figtree: '"Figtree", "Inter", "Segoe UI", system-ui, sans-serif',
  sora: '"Sora", "Inter", "Segoe UI", system-ui, sans-serif',
  rubik: '"Rubik", "Inter", "Segoe UI", system-ui, sans-serif',
  urbanist: '"Urbanist", "Inter", "Segoe UI", system-ui, sans-serif',
  ibmPlexSans: '"IBM Plex Sans", "Inter", "Segoe UI", system-ui, sans-serif',
  ibmPlexSerif: '"IBM Plex Serif", Georgia, serif',
  dmSerifDisplay: '"DM Serif Display", Georgia, serif',
  bodoniModa: '"Bodoni Moda", "Playfair Display", Georgia, serif',
  instrumentSerif: '"Instrument Serif", Georgia, serif',
};

export const DEFAULT_TYPOGRAPHY_PRESETS: Record<TypographyPresetKey, TypographyPreset> = {
  heroTitle: {
    fontFamilyKey: 'playfair',
    fontWeight: 500,
    letterSpacing: -0.02,
    lineHeight: 1.05,
    size: { mobile: 42, desktop: 86 },
    color: '#ffffff',
  },
  heroSubtitle: {
    fontFamilyKey: 'dmSans',
    fontWeight: 400,
    letterSpacing: -0.005,
    lineHeight: 1.35,
    size: { mobile: 18, desktop: 30 },
    color: '#f5f5f4',
  },
  heroMicroLine: {
    fontFamilyKey: 'inter',
    fontWeight: 500,
    letterSpacing: 0.01,
    lineHeight: 1.35,
    size: { mobile: 13, desktop: 15 },
    color: '#f5f5f4',
  },
  sectionHeading: {
    fontFamilyKey: 'playfair',
    fontWeight: 500,
    letterSpacing: -0.01,
    lineHeight: 1.15,
    size: { mobile: 28, desktop: 44 },
    color: '#1c1917',
  },
  body: {
    fontFamilyKey: 'inter',
    fontWeight: 400,
    letterSpacing: 0,
    lineHeight: 1.55,
    size: { mobile: 16, desktop: 18 },
    color: '#292524',
  },
  button: {
    fontFamilyKey: 'dmSans',
    fontWeight: 600,
    letterSpacing: 0.01,
    lineHeight: 1.2,
    size: { mobile: 14, desktop: 16 },
    color: '#ffffff',
  },
  caption: {
    fontFamilyKey: 'inter',
    fontWeight: 500,
    letterSpacing: 0.01,
    lineHeight: 1.3,
    size: { mobile: 12, desktop: 13 },
    color: '#57534e',
  },
};

export const DEFAULT_HERO_READABILITY_DEFAULTS: HeroReadabilityDefaults = {
  backdrop: 'subtle',
  textShadow: 'soft',
  autoContrast: true,
};

export const DEFAULT_DESIGN_SYSTEM_TYPOGRAPHY: DesignSystemTypography = {
  fontLibrary: DEFAULT_FONT_LIBRARY,
  presets: DEFAULT_TYPOGRAPHY_PRESETS,
  heroReadabilityDefaults: DEFAULT_HERO_READABILITY_DEFAULTS,
};

const clampNumber = (value: unknown, min: number, max: number, fallback: number) => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(max, Math.max(min, numeric));
};

const clampHexColor = (value: unknown, fallback?: string): string | undefined => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  return trimmed;
};

export function normalizeTypographyPreset(
  value: unknown,
  fallback: TypographyPreset,
  fontLibrary: Record<string, string>
): TypographyPreset {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const sizeRaw =
    raw.size && typeof raw.size === 'object' ? (raw.size as Record<string, unknown>) : {};

  let fontFamilyKey =
    typeof raw.fontFamilyKey === 'string' && raw.fontFamilyKey.trim()
      ? raw.fontFamilyKey.trim()
      : fallback.fontFamilyKey;

  if (!fontLibrary[fontFamilyKey]) {
    const fallbackKey = fontLibrary[fallback.fontFamilyKey] ? fallback.fontFamilyKey : Object.keys(fontLibrary)[0] || 'inter';
    fontFamilyKey = fallbackKey;
  }

  return {
    fontFamilyKey,
    fontWeight: clampNumber(raw.fontWeight, MIN_FONT_WEIGHT, MAX_FONT_WEIGHT, fallback.fontWeight),
    letterSpacing: clampNumber(raw.letterSpacing, MIN_LETTER_SPACING, MAX_LETTER_SPACING, fallback.letterSpacing),
    lineHeight: clampNumber(raw.lineHeight, MIN_LINE_HEIGHT, MAX_LINE_HEIGHT, fallback.lineHeight),
    size: {
      mobile: clampNumber(sizeRaw.mobile, MIN_FONT_SIZE, MAX_FONT_SIZE, fallback.size.mobile),
      desktop: clampNumber(sizeRaw.desktop, MIN_FONT_SIZE, MAX_FONT_SIZE, fallback.size.desktop),
    },
    color: clampHexColor(raw.color, fallback.color),
  };
}

export function getTypographyPreset(
  designSystem: Partial<DesignSystemTypography> | null | undefined,
  presetKey: TypographyPresetKey
): TypographyPreset {
  const fontLibrary = {
    ...DEFAULT_FONT_LIBRARY,
    ...(designSystem?.fontLibrary ?? {}),
  };
  const fallback = DEFAULT_TYPOGRAPHY_PRESETS[presetKey];
  const source = designSystem?.presets?.[presetKey];
  return normalizeTypographyPreset(source, fallback, fontLibrary);
}

export function isTypographyPresetKey(value: unknown): value is TypographyPresetKey {
  return typeof value === 'string' && (TYPOGRAPHY_PRESET_KEYS as string[]).includes(value);
}

export function resolveTypographyPresetKey(
  value: unknown,
  fallback: TypographyPresetKey
): TypographyPresetKey {
  return isTypographyPresetKey(value) ? value : fallback;
}

export function resolveTypographyStyle(
  preset: TypographyPreset,
  fontLibrary: Record<string, string>,
  options?: {
    fluid?: boolean;
  }
): React.CSSProperties {
  const fontFamily = fontLibrary[preset.fontFamilyKey] ?? DEFAULT_FONT_LIBRARY.inter;
  const useFluidSizing = options?.fluid !== false;
  const minViewport = 375;
  const maxViewport = 1280;
  const delta = Math.max(0, preset.size.desktop - preset.size.mobile);
  const viewportSpan = maxViewport - minViewport;
  const fluidExpression = `calc(${preset.size.mobile}px + ${delta} * ((100vw - ${minViewport}px) / ${viewportSpan}))`;

  return {
    fontFamily,
    fontWeight: preset.fontWeight,
    letterSpacing: `${preset.letterSpacing}em`,
    lineHeight: preset.lineHeight,
    fontSize: useFluidSizing
      ? `clamp(${preset.size.mobile}px, ${fluidExpression}, ${preset.size.desktop}px)`
      : `${preset.size.desktop}px`,
    color: preset.color,
  };
}
